import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardService } from '../../services/dashboardService';
import { 
  BarChart3, 
  Users, 
  MessageSquare, 
  TrendingUp,
  Activity,
  Calendar,
  Target,
  Zap,
  Loader2
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsData, activitiesData] = await Promise.all([
        dashboardService.getDashboardStats(),
        dashboardService.getRecentActivities(5)
      ]);
      
      // Format stats for display
      const formattedStats = [
        {
          name: 'Total Contacts',
          value: formatNumber(statsData.totalContacts),
          change: statsData.totalContactsChange || '0%',
          changeType: statsData.totalContactsChangeType || 'positive',
          icon: Users,
          color: 'bg-blue-500'
        },
        {
          name: 'Total Campaigns',
          value: formatNumber(statsData.activeCampaigns),
          change: statsData.activeCampaignsChange || '0',
          changeType: statsData.activeCampaignsChangeType || 'positive',
          icon: Target,
          color: 'bg-green-500'
        },
        {
          name: 'Messages Sent',
          value: formatNumber(statsData.messagesSent),
          change: statsData.messagesSentChange || '0%',
          changeType: statsData.messagesSentChangeType || 'positive',
          icon: MessageSquare,
          color: 'bg-purple-500'
        },
        {
          name: 'Response Rate',
          value: `${statsData.responseRate.toFixed(1)}%`,
          change: statsData.responseRateChange || '0%',
          changeType: statsData.responseRateChangeType || 'positive',
          icon: TrendingUp,
          color: 'bg-orange-500'
        }
      ];
      
      // Format activities with icons
      const formattedActivities = activitiesData.map(activity => {
        let icon, color;
        switch (activity.type) {
          case 'contact_added':
            icon = Users;
            color = 'text-blue-500';
            break;
          case 'campaign_started':
            icon = Target;
            color = 'text-green-500';
            break;
          case 'message_sent':
            icon = MessageSquare;
            color = 'text-purple-500';
            break;
          case 'template_created':
            icon = MessageSquare;
            color = 'text-orange-500';
            break;
          default:
            icon = Activity;
            color = 'text-gray-500';
        }
        return {
          id: activity.id,
          type: activity.type,
          message: activity.message,
          time: activity.relativeTime,
          icon,
          color
        };
      });
      
      setStats(formattedStats);
      setRecentActivities(formattedActivities);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-8 pt-8 pb-6">
        <div className="space-y-6">
          {/* Welcome Header - Compact Design */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg flex items-center justify-center">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Welcome, {user?.customerName}!</h1>
              </div>
            </div>
            
            {/* Quick Actions - Prominent Location */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => navigate('/contact-management')}
                className="flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                <Users className="h-4 w-4 mr-2" />
                Add Contacts
              </button>
              <button
                onClick={() => navigate('/templates/create')}
                className="flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors shadow-sm"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Create Template
              </button>
              <button
                onClick={() => navigate('/campaigns/new')}
                className="flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
              >
                <Target className="h-4 w-4 mr-2" />
                Start Campaign
              </button>
            </div>
          </div>

          {/* Stats Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="card">
                  <div className="card-body">
                    <div className="flex items-center justify-center h-24">
                      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="card">
              <div className="card-body">
                <p className="text-red-600">{error}</p>
              </div>
            </div>
          ) : stats ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.name} className="card">
                  <div className="card-body">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                        <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                      </div>
                      <div className={`h-12 w-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                        <stat.icon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div className="mt-4 flex items-center">
                      <span className={`text-sm font-medium ${
                        stat.changeType === 'positive' ? 'text-success-600' : 'text-error-600'
                      }`}>
                        {stat.change}
                      </span>
                      <span className="text-sm text-gray-500 ml-2">from last month</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Chart Section */}
            <div className="lg:col-span-2">
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-gray-900">Message Activity</h3>
                </div>
                <div className="card-body">
                  <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">Chart visualization coming soon</p>
                      <p className="text-sm text-gray-400">Message activity over time will be displayed here</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div>
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
                </div>
                <div className="card-body">
                  {loading ? (
                    <div className="flex items-center justify-center h-48">
                      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                  ) : recentActivities.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No recent activities</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {recentActivities.map((activity) => (
                        <div key={activity.id} className="flex items-start space-x-3">
                          <div className={`${activity.color} mt-1`}>
                            <activity.icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900">{activity.message}</p>
                            <p className="text-xs text-gray-500">{activity.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};
export default Dashboard;
